import React, { useMemo, useState } from 'react';

import {
  CartesianGrid,
  Tooltip as ChartTooltip,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';

import { Alert, Box, CircularProgress, Paper, Typography } from '@mui/material';

import type { ChainLpcLppSupplierDailyTrendPoint } from '~/types/services/priceParrot';

import { fmtShortDateWithYear } from '../PriceParrotParityTool/utils';
import TogglePillGroup from '../shared/TogglePillGroup';

import { TrendGranularity, bucketDailyTrend } from './trendBucketing';
import { errMsg } from './utils';

interface Props {
  daily: ReadonlyArray<ChainLpcLppSupplierDailyTrendPoint>;
  isFetching: boolean;
  isError: boolean;
  error: unknown;
}

const GRANULARITY_OPTIONS: ReadonlyArray<{ value: TrendGranularity; label: string }> = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'yearly', label: 'Yearly' },
];

/** Chain-wide LPC:LPP ratio and %LPC-vs-%LPP over time, rolled up across every selected chain. */
export default function RollupTrendChart({ daily, isFetching, isError, error }: Props) {
  const [granularity, setGranularity] = useState<TrendGranularity>('weekly');

  const rows = useMemo(() => bucketDailyTrend(daily, granularity), [daily, granularity]);

  return (
    <Paper elevation={0} sx={{ borderRadius: '12px', border: 1, borderColor: 'divider', p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 700 }}>Rolled-up overview over time</Typography>
        <TogglePillGroup<TrendGranularity>
          label="Granularity"
          ariaLabel="Chart granularity"
          options={GRANULARITY_OPTIONS}
          value={granularity}
          onChange={setGranularity}
        />
      </Box>

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load rollup trend: {errMsg(error)}
        </Alert>
      )}

      <Box sx={{ position: 'relative', minHeight: 320 }}>
        {isFetching && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        )}
        {!isFetching && !isError && rows.length === 0 && (
          <Typography color="text.secondary" sx={{ py: 4 }}>
            No daily data for the selected chain(s) and range.
          </Typography>
        )}
        {!isFetching && rows.length > 0 && (
          <ResponsiveContainer width="100%" height={320}>
            <LineChart data={rows}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="bucket"
                tick={{ fontSize: 11 }}
                interval="preserveStartEnd"
                tickFormatter={(v) => fmtShortDateWithYear(String(v))}
              />
              <YAxis tick={{ fontSize: 11 }} />
              <ChartTooltip
                labelFormatter={(label) => fmtShortDateWithYear(String(label))}
                formatter={(value: number | string) => (value == null || value === '' ? '—' : Number(value).toFixed(2))}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="ratio"
                name="LPC:LPP ratio"
                stroke="#2563eb"
                strokeWidth={2}
                dot={false}
                connectNulls={false}
              />
              <Line
                type="monotone"
                dataKey="pctLpc"
                name="% LPC (views)"
                stroke="#16a34a"
                strokeWidth={2}
                dot={false}
                connectNulls={false}
              />
              <Line
                type="monotone"
                dataKey="pctLpp"
                name="% LPP (views)"
                stroke="#d97706"
                strokeWidth={2}
                dot={false}
                connectNulls={false}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </Box>
    </Paper>
  );
}
